// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Software Risk Management Dashboard
 *
 * Shows list of SRM requests (document groups by Workday REQ#).
 * Supports navigation to detail view via URL search params.
 */
import { useCallback, useMemo, useState, type FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { NodeUserViewProps } from '@/core/types'
import { useServices } from '@/app-shell/services'
import { Loader2, Plus } from 'lucide-react'
import { Button } from '@/components/ui'
import { ConfirmDialog } from '@/components/forms'
import { toast } from 'sonner'
import { usePipelineRunner } from '@/applications/shared'
import { ConnectorSyncStrip } from '@/subsystems/vault/connectors/components/ConnectorSyncStrip'
import { useAppConfig, useRequests } from '../hooks'
import { deleteRequest } from '../services/api'
import { pendingConnectorRuns } from '../utils'
import { RequestList } from './RequestList'
import { RequestDetail } from './RequestDetail'
import { NewRequestDialog } from './NewRequestDialog'
import { DocsDialog } from './DocsDialog'
import type { SrmRequest } from '../types'

export const SrmUserView: FC<NodeUserViewProps> = ({ orgId, workspaceId }) => {
  const services = useServices()
  const apiClient = services.getClient()
  const { agents, slotsEnabled } = useAppConfig(apiClient, orgId, workspaceId)
  const [searchParams, setSearchParams] = useSearchParams()
  const [newRequestOpen, setNewRequestOpen] = useState(false)
  const [pendingRunOpen, setPendingRunOpen] = useState(false)
  const [submittingPending, setSubmittingPending] = useState(false)

  const selectedRequestId = searchParams.get('request')

  const { requests, isLoading, isError, error, refetch } = useRequests(
    apiClient,
    orgId,
    workspaceId,
  )

  const selectedRequest = useMemo(() => {
    if (!selectedRequestId) return null
    return requests.find((r) => r.groupId === selectedRequestId) ?? null
  }, [requests, selectedRequestId])
  const pendingRuns = useMemo(
    () => pendingConnectorRuns(requests, agents),
    [agents, requests],
  )
  const pendingRequestCount = useMemo(
    () => new Set(pendingRuns.map(plan => plan.request.groupId)).size,
    [pendingRuns],
  )
  const bulkRunner = usePipelineRunner({
    apiClient,
    orgId,
    wsId: workspaceId,
    appId: 'srm',
    onAllComplete: () => refetch(),
  })

  const handleSelectRequest = useCallback((request: SrmRequest) => {
    setSearchParams((prev) => {
      prev.set('request', request.groupId)
      return prev
    })
  }, [setSearchParams])

  const handleBack = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('request')
      return prev
    })
  }, [setSearchParams])

  const handleDeleteRequest = useCallback(async (request: SrmRequest) => {
    try {
      await deleteRequest(apiClient, orgId, workspaceId, request.groupId)
      toast.success('Request deleted', { description: `${request.groupId} has been removed` })
      refetch()
    } catch (err) {
      toast.error('Failed to delete request', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
      throw err
    }
  }, [apiClient, orgId, workspaceId, refetch])

  const handleRunPending = useCallback(async () => {
    if (pendingRuns.length === 0) return
    setSubmittingPending(true)
    let queued = 0
    try {
      for (const plan of pendingRuns) {
        await bulkRunner.run(plan.documentIds, {
          agentId: plan.agent.agentId,
          groupId: plan.request.groupId,
        })
        queued += 1
      }
      setPendingRunOpen(false)
      refetch()
      toast.success('Pending analysis started', {
        description: `${queued} ${queued === 1 ? 'analysis' : 'analyses'} queued across ${pendingRequestCount} ${pendingRequestCount === 1 ? 'request' : 'requests'}`,
      })
    } catch (err) {
      toast.error('Failed to start all pending analysis', {
        description: queued > 0
          ? `${queued} ${queued === 1 ? 'analysis was' : 'analyses were'} queued before the failure.`
          : err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSubmittingPending(false)
    }
  }, [bulkRunner, pendingRequestCount, pendingRuns, refetch])

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading requests...</span>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="text-destructive">
          Failed to load requests: {error?.message ?? 'Unknown error'}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <ConnectorSyncStrip
        api={apiClient}
        orgId={orgId}
        workspaceId={workspaceId}
        pending={{
          count: pendingRequestCount,
          singular: 'request',
          plural: 'requests',
          running: submittingPending || bulkRunner.activeJobs.length > 0,
          onRun: () => setPendingRunOpen(true),
        }}
      />

      {selectedRequest ? (
        <RequestDetail
          request={selectedRequest}
          agents={agents}
          onBack={handleBack}
          apiClient={apiClient}
          orgId={orgId}
          wsId={workspaceId}
          onRefresh={refetch}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold" data-testid="srm-heading">Software Risk Management</h1>
            <div className="flex items-center gap-2">
              <DocsDialog />
              <Button size="sm" onClick={() => setNewRequestOpen(true)} data-testid="srm-new-request">
                <Plus className="h-4 w-4 mr-2" />
                New Request
              </Button>
            </div>
          </div>
          <RequestList
            requests={requests}
            agents={agents}
            onSelectRequest={handleSelectRequest}
            onDeleteRequest={handleDeleteRequest}
          />
        </>
      )}

      <ConfirmDialog
        open={pendingRunOpen}
        onOpenChange={setPendingRunOpen}
        title="Run all pending connector requests?"
        description={`This will start ${pendingRuns.length} ${pendingRuns.length === 1 ? 'analysis' : 'analyses'} across ${pendingRequestCount} synced ${pendingRequestCount === 1 ? 'request' : 'requests'}. Already current and actively running reviews are excluded.`}
        confirmLabel={`Run ${pendingRuns.length} ${pendingRuns.length === 1 ? 'analysis' : 'analyses'}`}
        loading={submittingPending}
        onConfirm={handleRunPending}
        testId="srm-run-pending-confirm"
      />

      {!selectedRequest && (
        <NewRequestDialog
          open={newRequestOpen}
          onOpenChange={setNewRequestOpen}
          apiClient={apiClient}
          orgId={orgId}
          wsId={workspaceId}
          agents={agents}
          slotsEnabled={slotsEnabled}
          onSuccess={refetch}
        />
      )}
    </div>
  )
}
